import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Check, AlertTriangle, AlertCircle } from 'lucide-react';
import { inspectionService } from '../services/inspectionService';
import { InspectionRecord } from '../types';

const PageContainer = styled.div`
  padding: 30px;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  div {
    h1 {
      font-size: 32px;
      font-weight: 700;
      color: #1a1f3a;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #6b7280;
      margin: 0;
    }
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 153, 255, 0.3);
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    text-align: left;
    padding: 12px;
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
  }
  
  td {
    padding: 12px;
    border-top: 1px solid #f3f4f6;
    color: #1a1f3a;
    font-size: 14px;
    vertical-align: middle;
  }
  
  tr {
    transition: background 0.2s ease;
    
    &:hover {
      background: #f9fafb;
    }
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'verified': return '#d1fae5';
      case 'repaired': return '#dbeafe';
      default: return '#fee2e2';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'verified': return '#059669';
      case 'repaired': return '#1e40af';
      default: return '#dc2626';
    }
  }};
`;

const Thumb = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
`;

const InspectionsList: React.FC = () => {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState<InspectionRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInspections();
  }, []);

  const loadInspections = async () => {
    try {
      const data = await inspectionService.getAll();
      setInspections(data);
    } catch (error) {
      console.error('Failed to load inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddInspection = () => {
    navigate('/inspection');
  };

  // Helper to render severity stars/indicator
  const renderSeverity = (severity: number) => {
    const dots = [];
    for (let i = 0; i < 5; i++) {
      dots.push(
        <span key={i} style={{
          color: i < severity ? '#ef4444' : '#e5e7eb',
          fontSize: '16px',
          marginRight: '2px'
        }}>•</span>
      );
    }
    return <div>{dots} <span style={{ fontSize: '12px', color: '#6b7280' }}>({severity})</span></div>;
  };

  return (
    <PageContainer>
      <Header>
        <div>
          <h1>INSPECTIONS</h1>
          <p>Global list of all detected damages and annotations</p>
        </div>
        <AddButton onClick={handleAddInspection}>
          <Plus size={18} />
          New Inspection
        </AddButton>
      </Header>

      <Section>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading inspections database...
          </div>
        ) : inspections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
            <AlertCircle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <h3>No inspections found</h3>
            <p>Annotate images and verify damage to see them listed here.</p>
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Image</th>
                <th>Wind Farm</th>
                <th>Turbine</th>
                <th>Blade / Side</th>
                <th>Damage Type</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {inspections.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/inspection?project=${item.projectId}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <Thumb src={item.thumbnailUrl || item.imageUrl} alt="Damage" />
                  </td>
                  <td>{item.windFarmName || '-'}</td>
                  <td>{item.turbineName}</td>
                  <td>{item.bladeName} <span style={{ color: '#9ca3af' }}>/</span> {item.surfaceType}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{item.damageType}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{item.comment ? item.comment.substring(0, 30) + '...' : ''}</div>
                  </td>
                  <td>{renderSeverity(item.severity)}</td>
                  <td>
                    <StatusBadge $status={item.status}>
                      {item.status.toUpperCase()}
                    </StatusBadge>
                  </td>
                  <td style={{ color: '#6b7280', fontSize: '12px' }}>
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </PageContainer>
  );
};

export default InspectionsList;
